import {
  Banknote,
  CircleDollarSign,
  Coins,
  Hourglass,
  MousePointerClick,
  Percent,
  ShoppingBag,
  UserPlus,
} from "lucide-react";

import { pctChange, StatTile } from "@/components/stat-tile";
import { money } from "@/lib/format";
import type { PerformanceData } from "@/lib/types";
import {
  comparisonReachesPastRetention,
  count,
  rangeReachesPastRetention,
  rate,
} from "@/pages/performance/format";

const CLICKS_GONE = "Older clicks are deleted";

/**
 * The eight KPIs of the range, each against the previous period of the same
 * length. The tiles stand on the page: 2 columns on a phone, 4 on a desktop.
 * `pendingCents` comes from the overview read; null while it is unknown.
 *
 * Clicks are deleted after `clickRetentionDays`, so the three figures built on
 * them lose their delta when the previous period reaches past that (it would
 * read as a false drop), and the two rates show a dash when the range itself
 * does (the clicks kept are the ones that converted, so they would read high).
 */
export function KpiTiles({
  data,
  pendingCents,
}: {
  data: PerformanceData;
  pendingCents: number | null;
}) {
  const t = data.totals;
  const p = data.previous;
  const days = data.days;
  const noClickDelta = comparisonReachesPastRetention(
    days,
    data.clickRetentionDays,
  );
  const noClickRates = rangeReachesPastRetention(days, data.clickRetentionDays);
  return (
    <div
      data-slot="kpi-tiles"
      className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4"
    >
      <StatTile
        label="Clicks"
        icon={MousePointerClick}
        value={count(t.clicks)}
        delta={noClickDelta ? undefined : pctChange(t.clicks, p.clicks)}
        days={days}
      />
      <StatTile
        label="Sign-ups"
        icon={UserPlus}
        value={count(t.signups)}
        delta={pctChange(t.signups, p.signups)}
        days={days}
      />
      <StatTile
        label="First orders"
        icon={ShoppingBag}
        value={count(t.firstOrders)}
        delta={pctChange(t.firstOrders, p.firstOrders)}
        days={days}
      />
      <StatTile
        label="Conversion rate"
        icon={Percent}
        value={noClickRates ? "—" : rate(t.conversionRate)}
        delta={
          noClickDelta
            ? undefined
            : pctChange(t.conversionRate, p.conversionRate)
        }
        days={days}
        hint={noClickRates ? CLICKS_GONE : "Clicks that led to a first order"}
      />
      <StatTile
        label="Fees earned"
        icon={CircleDollarSign}
        value={money(t.earnedCents)}
        delta={pctChange(t.earnedCents, p.earnedCents)}
        days={days}
      />
      <StatTile
        label="Paid to you"
        icon={Banknote}
        value={money(t.paidCents)}
        delta={pctChange(t.paidCents, p.paidCents)}
        days={days}
        hint="Payouts we sent in this range"
      />
      <StatTile
        label="Earnings per click"
        icon={Coins}
        value={noClickRates ? "—" : money(t.earningsPerClickCents)}
        delta={
          noClickDelta
            ? undefined
            : pctChange(t.earningsPerClickCents, p.earningsPerClickCents)
        }
        days={days}
        hint={noClickRates ? CLICKS_GONE : undefined}
      />
      <StatTile
        label="Pending balance"
        icon={Hourglass}
        value={pendingCents === null ? "—" : money(pendingCents)}
        hint="Fees in the hold period, today"
      />
    </div>
  );
}
